<script>
    import table from './ib-table-basic.vue'

    /**
     * Extension of [ib-table-basic](#ibtablebasic) with row groups that can collapse or expand.
     */
    export default {
        name: 'ib-table-group',
        mixins: [table],

        props: {
            /**
             * An array of currently expanded table rows by id.
             */
            value: [String,Number,Array],
            /**
             * Member of data object to use as emitted value, or function to retrieve such. This is useful if using v-model to control expanded rows.
             */
            emit: [String,Number,Function],
            /**
             * Member of data object to use as expandable child, or function to retrieve such. By default the 'child' member is used.
             */
            child: [String,Number,Function],
            /**
             * If true, allow multiple rows to be expanded simultaneously.
             */
            multiple: Boolean,
            /**
             * If true, a click in the row not caught by another element (e.g. input) will expand the row.
             */
            rowhook: Boolean
        },

        data() {
            return {
                selected: []
            };
        },

        computed: {
            getid() {
                const emit = this.emit;
                if (typeof(emit) === 'function') {
                    return emit;
                } else if (emit != null) {
                    return row => row[emit];
                } else {
                    return (row,index) => index;
                }
            },

            getchild() {
                const child = this.child;
                return typeof child == 'function' ? child : child == null ? data => data.child : data => data[child];
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (value == null) {
                        this.selected = [];
                    } else {
                        if (value instanceof Array) {
                            this.selected = value;
                        } else {
                            this.selected = [value];
                        }
                    }
                },
                immediate: true
            },

            v(visible) {
                const sel = this.selected.filter((i) => visible[i]);
                if (sel.length != this.selected.length) {
                    this.$emit('input', (this.selected = sel).slice());
                }
            }
        },

        methods: {
            renderHeadBefore(h) {
                return [
                    h('ib-table-col', {
                        'staticClass': '_tbsid',
                        'attrs': { sticky: 'start', width: '32px', force: true }
                    })];
            },

            renderBody(h) {
                const body = this.renderSlot('body');
                const child = this.renderSlot('child');
                return this.sorted.reduce((el,row,index) => {
                    const hasChild = this.getchild(row.data) != null;
                    el.push(h('tbody', {
                        'class': this.selected.includes(row.id) ? '_tbgex' : null
                    }, [
                        h('tr', {
                            'staticClass': '_tbgr'
                        }, [
                            ...this.renderBodyStart(h, row),
                            h('td', {
                                'staticClass': '_tbsid mirror-rtl'
                            }, hasChild ? [
                                h('a', {
                                    'attrs': { role: 'button' },
                                    'staticClass': '_tbga',
                                    'on': this.rowhook ? null : { click: this.click }
                                }, [
                                    h('svg', { attrs: { viewBox: '0 0 50 50' } },[ h('path', { attrs: { d: 'M16 5v42l22-21z' }}) ])
                                ])
                            ] : void 0),
                            body(row.data),
                            ...this.renderBodyEnd(h, row)
                        ])
                    ]));
                    if (hasChild) {
                        el.push(h('tbody', {
                            'staticClass': '_tbgch'
                        }, [
                            h('tr', [
                                ...this.renderBodyStart(h, row.data),
                                h('td', {
                                    'staticClass': '_tbsid'
                                }, [
                                    h('div', {
                                        'staticClass': '_tbgln'
                                    })
                                ]),
                                child(row.data),
                                ...this.renderBodyEnd(h, row.data)
                            ])
                        ]));
                    }
                    return el;
                }, []);
            },

            toggle(id) {
                if (this.selected.includes(id)) {
                    this.selected = this.selected.filter(e => e != id);
                } else {
                    this.selected.push(id)
                }
            },

            eclick(e) {
                const td = this.td(e);
                const tr = this.tr(td);
                if (tr) {
                    const ri = [].indexOf.call(this.$el.querySelectorAll('tr._tbgr'), tr);
                    const ci = [].indexOf.call(tr.children, td);
                    this.clickhook(ri, ci);
                    this.clickemit(ri, ci);
                }
            },

            /** @private */
            clickhook(ri, ci) {
                if (ri >= 0) {
                    this.toggle(this.sorted[ri].id);
                }
            },

            /** @private */
            hoverhook() {
                //TODO
            },

            /** @private */
            before() {
                const start = { width: 0 };
                const arrow = { width: '32px', sticky: 'start', force: 'true' };
                return this.stickyStart >= 0 ? [ start, arrow ] : [ arrow ];
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tb {
        ._tbga {
            vertical-align: middle;
            > svg {
                width: 14px;
                height: 14px;
                vertical-align: -1px;
                transition: transform .15s linear;
                fill: @fg-accent;
            }
        }
        ._tbgch {
            display: none;
        }
        ._tbgex {
            > tr > td {
                border-bottom: none;
                box-shadow: none;
            }
            ._tbga {
                display: inline-block;
                width: 32px;
                > svg {
                    transform: rotate(90deg);
                }
            }
            + ._tbgch {
                display: table-row-group;
            }
        }
        ._tbgln {
            position: absolute;
            top: 4px;
            bottom: 4px;
            left: 15.5px;
            border-right: 1px solid fade(@fg-accent, 25%);
        }
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() { return {
                sample1: [
                    { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                    { currency: 'GBP', amount: 35, note: 'Thirty-five British pounts' },
                    { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                    { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                    { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                    { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                    { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                    { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                    { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                ],
                value1: null,
                value2: []
            }},
            template:
                `<ib-context>
                    <p class="after-md">Single select: {{value1}}</p>
                    <ib-table-group :data="sample1" v-model="value1" emit="currency">
                        <ib-table-col width="8ch" sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col width="24ch">Note</ib-table-col>
                        <ib-table-col width="12ch" sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-group>
                </ib-context>`
        });
    ```
</docs>
